/**
 * LE CONTRAT DE MODULE — pièce maîtresse du « totalement évolutif ».
 *
 * Un module = un dossier `src/modules/<id>/` exportant `default` un ModuleDef.
 * Le noyau (App, Shell, Fiche360Layout) DÉRIVE tout du registre : routes,
 * sidebar, widgets de la fiche 360, badges. Ajouter un module ne modifie
 * AUCUN fichier de `core/` — garanti par le test d'invariants
 * (registry.invariants.test.ts) qui échoue si une route ou un libellé
 * apparaît en dur dans le noyau.
 *
 * Leçon de l'app Courses : son registre était déclaré mais consommé nulle
 * part (routes et sidebar écrites à la main). Ici la découverte est
 * STRUCTURELLE (import.meta.glob) — il n'existe pas d'autre chemin.
 */
import type { NavGroupId, Role } from "@geceram/shared";
import type { ComponentType, LazyExoticComponent } from "react";

/** Zones de contribution à la fiche client 360 (bible §9). */
export type WidgetZone = "kpi" | "timeline" | "sidebar";

export interface Widget360 {
  /** Unique, préfixé par l'id du module : "organisations.kpi". */
  id: string;
  zone: WidgetZone;
  /** Tri intra-zone (10, 20, 30… — laisser de l'espace). */
  order: number;
  /** Absent = hérite des rolesRequis du module. */
  roles?: readonly Role[];
  component: LazyExoticComponent<ComponentType<{ cabinetId: string }>>;
}

export interface ModuleRoute {
  /** Chemin absolu ("/cabinets", "/cabinets/:id"). */
  path: string;
  component: LazyExoticComponent<ComponentType>;
}

export interface ModuleBadge {
  /** Clé TanStack Query, préfixée par l'id du module. */
  queryKey: readonly string[];
  /** Nombre à afficher (null = pas de pastille). */
  queryFn: () => Promise<number | null>;
  refetchIntervalMs: number;
}

/** La question qu'un module pose à l'entrée — voir `ModuleDef.accueil`. */
export interface ModuleAccueil {
  /** Clé TanStack Query, préfixée par l'id du module. */
  queryKey: readonly string[];
  /** Chemin à ouvrir, ou `null` pour « accueil normal ». */
  queryFn: () => Promise<string | null>;
}

export interface ModuleDef {
  /** Identifiant unique — aussi le nom du dossier. */
  id: string;
  /** Libellé de la sidebar. */
  label: string;
  /** Id de symbole du sprite d'icônes (core/icons.tsx), ex. "i-building". */
  icon: string;
  navGroup: NavGroupId;
  /** Tri global dans la sidebar (au sein du groupe). */
  navOrder: number;
  /** Vide = tous les rôles authentifiés. */
  rolesRequis: readonly Role[];
  /** Première route = destination du clic sidebar. */
  routes: readonly ModuleRoute[];
  /**
   * ROUTES SERVIES HORS SESSION — arrivé avec É8 (lien public par jeton).
   *
   * ⚠ CE CHAMP OUVRE DES PAGES À DES INCONNUS : c'est le seul du contrat dont
   * l'oubli d'un contrôle se paie en fuite de données, et non en écran vide.
   * Ce qui le borne :
   *
   *  - `rolesRequis` NE S'Y APPLIQUE PAS, par définition — le porteur d'un lien
   *    n'a pas de compte, donc pas de rôle. C'est voulu, et c'est pour cela que
   *    le champ est séparé de `routes` : deux listes, deux régimes, et personne
   *    ne peut rendre une page publique par mégarde en l'ajoutant à la mauvaise ;
   *
   *  - l'autorisation réelle vit **dans le serveur**, pas ici. La page ne montre
   *    que ce que la route publique du BFF veut bien lui donner contre un jeton.
   *
   * ⚠ POURQUOI CE CHAMP PLUTÔT QU'UN IMPORT DEPUIS `core/`. Ma première version
   * importait la page publique du module directement dans `App.tsx`. La règle D8
   * du cahier des charges l'a REFUSÉE : le noyau n'atteint jamais un fichier
   * interne d'un module, sinon le module cesse d'être déplaçable. Le module
   * DÉCLARE donc ses pages publiques par son point d'accroche, et le noyau les
   * monte sans savoir d'où elles viennent.
   */
  routesPubliques?: readonly ModuleRoute[];
  /**
   * Module atteint par lien direct seulement : ses routes sont montées, il
   * n'apparaît jamais dans la sidebar. Premier usage : la fiche de travail
   * `/travaux/:code`.
   */
  sansNav?: boolean;
  /**
   * LIEN DANS LE PIED DE LA COQUE — arrivé avec C13 (page « Bienvenue »).
   *
   * Une page qu'on rouvre rarement mais qui doit rester atteignable n'a pas sa
   * place dans la barre de navigation : elle y prendrait le rang d'un module de
   * travail. Elle vit donc dans le pied, à côté de la déconnexion.
   *
   * ⚠ SE POSE AVEC `sansNav`, JAMAIS SEUL. Deux entrées pour la même page — une
   * dans la nav, une dans le pied — font hésiter avant chaque clic. L'invariant
   * `registry.invariants.test.ts` REFUSE la combinaison `lienPied` sans
   * `sansNav` ; ce n'est pas une convention, c'est un test qui casse.
   *
   * Le noyau ne nomme personne : `liensPied(registry, roles)` (core/accueil.ts)
   * dérive la liste, `LienPied` rend le lien depuis `mod.routes[0].path`,
   * `mod.icon` et `mod.label`. Ajouter un module au pied ne touche AUCUN
   * fichier de `core/` — c'est la règle D8 appliquée au pied.
   */
  lienPied?: boolean;
  /**
   * CAPTER L'ACCUEIL — « avant le tableau de bord, passe d'abord par ici ».
   *
   * ⚠ POURQUOI UNE FONCTION DU MODULE PLUTÔT QU'UN TEST DANS LE NOYAU. La
   * première version faisait lire au noyau le journal d'audit pour savoir si la
   * personne avait déjà vu la page de bienvenue. Le noyau aurait alors connu le
   * nom d'un événement d'audit, la forme de sa réponse, et la route à ouvrir :
   * trois liens durs vers un module — exactement ce que la règle D8 interdit.
   * Le module DÉCLARE sa question, le noyau se contente de la poser.
   *
   * `queryFn` rend le chemin à ouvrir, ou `null` pour « accueil normal ». Elle
   * est posée par TanStack Query (`queryKey`, `staleTime: Infinity`,
   * `retry: false`) : une seule lecture par session, pas de rejeu.
   *
   * ⚠ UNE PANNE NE FERME JAMAIS LA PORTE. Si la fonction échoue (503 sur le
   * journal, réseau coupé), l'entrée se fait sur l'accueil normal — voir
   * `destinationAccueil` et ses tests. Bloquer l'entrée du CRM sur la lecture
   * d'une trace serait faire dépendre le travail de tous d'un service annexe.
   *
   * Plusieurs modules peuvent capter : le PREMIER du registre gagne (ordre
   * groupe de nav, puis navOrder) — un ordre déjà visible à l'écran, donc
   * explicable, plutôt qu'un ordre de fichier.
   */
  accueil?: ModuleAccueil;
  widgets360?: readonly Widget360[];
  badge?: ModuleBadge;
  /** Modules externes (iframe) : état de santé → états dégradés §20. */
  healthcheck?: () => Promise<"ok" | "degraded" | "down">;
  /**
   * Nom d'une variable d'env Vite ("VITE_FF_WHATSAPP") : le module n'est
   * enregistré que si elle vaut "true". Absent = toujours actif.
   */
  featureFlag?: string;
}

export function canAccess(mod: ModuleDef, roles: readonly Role[]): boolean {
  return mod.rolesRequis.length === 0 || roles.some((r) => mod.rolesRequis.includes(r));
}

export function widgetAllowed(mod: ModuleDef, widget: Widget360, roles: readonly Role[]): boolean {
  const requis = widget.roles ?? mod.rolesRequis;
  return requis.length === 0 || roles.some((r) => requis.includes(r));
}
